<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px"/>
  </div>

  <alert-badge
      v-else-if="error.title"
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <div v-else class="flex flex-col md:flex-row">
    <div class="w-full mx-2 md:w-9/12">
      <div class="mt-2">
        <el-table
            :data="po.orderItems"
            stripe
            border
            style="width: 100%"
            height="300"
            :max-height="800"
            class="card rounded"
            header-cell-class-name="my-header"
        >
          <el-table-column :min-width="40" type="index" label="#"/>
          <el-table-column :min-width="90" prop="item.externalID"
                           :label="t('itemCode')"/>
          <el-table-column :min-width="220" :label="t('itemName')">
            <template #default="scope">
              <div class="truncate">
                {{ scope.row.item.name }}
              </div>
            </template>
          </el-table-column>

          <el-table-column :min-width="120" :label="t('quantity')">
            <template #default="scope">
              <div class="flex items-center">
                <div class="px-2 pt-1">
                  {{ scope.row.quantity }}
                </div>
                <el-popover
                    v-if="scope.row.orderItemSerials?.length"
                    placement="bottom-start"
                    :title="t('serialNumbers')"
                    :min-width="200"
                    trigger="hover"
                >
                  <template #default>
                    <p v-for="(serial, i) in scope.row.orderItemSerials"
                       :key="i">
                      {{ serial.itemSerial.number }}
                    </p>
                  </template>
                  <template #reference>
                    <div class="flex justify-center">
                      <svg
                          xmlns="http://www.w3.org/2000/svg"
                          xmlns:xlink="http://www.w3.org/1999/xlink"
                          height="16px"
                          width="16px"
                          version="1.1"
                          id="Capa_1"
                          viewBox="0 0 15.273 15.273"
                          xml:space="preserve"
                      >
                        <g>
                          <path
                              style="fill: #030104"
                              d="M0,1.803h0.897V13.47H0V1.803z M1.446,13.47h0.505V1.803H1.446V13.47z M2.315,13.47h0.504V1.803   H2.315V13.47z M3.482,13.47h1.104V1.803H3.482V13.47z M5.47,13.47h0.552V1.803H5.47V13.47z M6.878,13.47h2.288V1.803H6.878V13.47z    M12.84,13.47h0.752V1.803H12.84V13.47z M14.248,1.803V13.47h1.025V1.803H14.248z M10.084,13.47h0.492V1.803h-0.492V13.47z    M11.281,13.47h0.492V1.803h-0.492V13.47z"
                          />
                        </g>
                      </svg>
                    </div>
                  </template>
                </el-popover>
              </div>
            </template>
          </el-table-column>
          <el-table-column :min-width="220" :label="t('receivedQuantity')">
            <template #default="scope">
              <div class="truncate">
                {{ scope.row.receivedQuantity }}
              </div>
            </template>
          </el-table-column>
          <el-table-column :min-width="220" :label="t('notes')">
            <template #default="scope">
              <div class="truncate">
                {{ scope.row.notes }}
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="mt-4 flex flex-col justify-start text-lg">
        {{ t('orderJourney') }}
        <div class="mt-4 mx-5">
          <el-timeline>
            <el-timeline-item placement="bottom">
              <div>
                <div class="font-bold">{{ t('purchased') }}
                </div>
                <div
                    class="text-sm text-gray-400 flex justify-between items-center w-1/3">
                  <div>{{ formatPostgresDate(po.createdAt) }}</div>
                  <div>{{ po.user?.name }}</div>
                </div>
              </div>
            </el-timeline-item>
            <el-timeline-item v-for="(ir, index) in po?.inventoryReceivings" :key="ir.id"
                              placement="bottom">
              <div>
                <div class="font-bold">{{ t(irStatuses[index]) }}
                </div>
                <div
                    class="text-sm text-gray-400 flex justify-between items-center w-1/3">
                  <div>{{ formatPostgresDate(ir.createdAt) }}</div>
                  <div>{{ ir.user?.name }}</div>
                </div>
              </div>
            </el-timeline-item>
          </el-timeline>
        </div>
      </div>
    </div>

    <div class="w-full md:w-3/12 mt-2" style="font-size: 14px !important">
      <div class="flex flex-col items-center px-3" v-if="po">
        <div class="w-full">
          <div class="card rounded p-3">
            <div
                class="flex justify-between p-3 w-full bg-gray-100 rounded mt-1">
              <p>{{ t('purchaseOrderId') }}</p>
              <p class="flex justify-end custom-bold">
                {{ po?.externalID }}
              </p>
            </div>
            <div
                class="flex justify-between p-3 w-full bg-gray-100 rounded mt-1">
              <p>{{ t('vendor') }}</p>
              <p class="flex justify-end custom-bold">
                {{ po?.vendor?.name }}
              </p>
            </div>
            <div v-if="po?.customerID" class="flex justify-between p-3 w-full bg-orange-100/50 rounded mt-1">
              <p>{{ t('customer') }}</p>
              <p class="flex justify-end custom-bold">
                {{ po?.customer?.name }}
              </p>
            </div>
            <div
                v-if="po?.pouchID"
                class="flex justify-between p-3 mt-1 w-full bg-gray-100 rounded"
            >
              <p>{{ t('pouchID') }}</p>
              <p class="flex justify-end custom-bold">
                {{ po?.pouchID }}
              </p>
            </div>
            <div v-if="po?.referenceableExternalID"
                 class="flex justify-between p-3 w-full">
              <p>{{ t('orderID') }}</p>
              <p class="flex justify-end custom-bold">
                {{ po?.referenceableExternalID }}
              </p>
            </div>
            <div v-if="po?.manualNumber"
                 class="flex justify-between p-3 w-full">
              <p>{{ t('manualNumber') }}</p>
              <p class="flex justify-end custom-bold">
                {{ po?.manualNumber }}
              </p>
            </div>
            <div class="flex justify-between p-3 w-full">
              <p>{{ t('models.common.date') }}</p>
              <p class="flex justify-end custom-bold">
                {{ formatPostgresDate(po?.createdAt, 'DD/MM/YYYY hh:mm A') }}
              </p>
            </div>
            <div v-if="po?.dueDate" class="flex justify-between items-center p-3 w-full">
              <p>{{ t('dueDate') }}</p>
              <el-date-picker
                  style="width: 140px;"
                  v-model="po.dueDate"
                  type="date"
                  @change="updateDueDate"
              />
            </div>
            <div class="flex justify-between p-3 w-full">
              <p>{{ t('cashier') }}</p>
              <p class="flex justify-end custom-bold">
                {{ po?.user?.name }}
              </p>
            </div>
            <div class="flex justify-between w-full p-3">
              <p>{{ t('location') }}</p>
              <p class="flex justify-end custom-bold">
                {{
                  $ctx.locale === 'en' ? po?.location?.enName : po?.location?.arName
                }}
              </p>
            </div>
            <div class="flex justify-between w-full p-3">
              <p>{{ t('notes') }}</p>
              <p class="flex justify-end custom-bold">
                {{ po?.notes }}
              </p>
            </div>
          </div>
        </div>

        <div class="card rounded w-full p-3 mt-4 flex flex-col gap-2">
          <div class="flex flex-col items-center w-12/12 gap-1 text-black mt-1">
            <horizontal-button
                faIcon="print"
                :title="t('printOrder')"
                @click="router.push($Route.INVENTORY_PURCHASE_ORDERS_PURCHASE_ORDER_RECEIPT.replace(':id', po.id))"
            />
          </div>

          <div v-if="!isFullyReceived" class="flex flex-col items-center w-12/12 gap-1 text-black">
            <horizontal-button
                faIcon="cart-arrow-down"
                :title="t('receiveOrder')"
                @click="router.push($Route.INVENTORY_PURCHASE_ORDERS_NEW_INVENTORY_RECEIVING.replace(':id', po.id))"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, onBeforeMount, ref} from 'vue'

import {useHTTP} from '@/plugins/http'

// components
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import {useI18n} from 'vue-i18n'
import {useRoute, useRouter} from 'vue-router'

// helpers
import {formatPostgresDate} from '@/utils/date'
import HorizontalButton from '@/components/HorizontalButton.vue'
import {i18nOrderMessages} from '@/models/sales/Order'
import {itemI18nMessages} from '@/models/inventory/Item'
import {PurchaseOrder} from "@/models/inventory/PurchaseOrder";
import dayjs from "dayjs";

export default defineComponent({
  name: 'purchase-order',
  components: {
    LoadingSpinner,
    AlertBadge,
    HorizontalButton
  },
  setup() {
    const {t} = useI18n({
      messages: {
        en: {
          ...i18nOrderMessages.en,
          ...itemI18nMessages.en,
          printOrder: "Print Order",
          date: 'Date',
          cashier: 'Cashier',
          purchaseOrderId: "Purchase Order No",
          receivedQuantity: "Received Quantity",
          receiveOrder: "Receive Order",
          orderID: "Order No",
          notes: 'Notes',
          purchased: 'Purchased',
          partial: 'Partial Received',
          full: 'Received',
          manualNumber: 'Manual No',
          pouchID: 'Pouch ID'
        },
        ar: {
          ...i18nOrderMessages.ar,
          ...itemI18nMessages.ar,
          printOrder: "طباعة الطلب",
          date: 'التاريخ',
          cashier: 'المحاسب',
          purchaseOrderId: "رقم طلب الشراء",
          receivedQuantity: "الكمية المستلمة",
          receiveOrder: "استلام الطلب",
          orderID: "رقم الطلب",
          notes: "الملاحظات",
          purchased: 'طلب شراء',
          partial: 'مستلم جزئيا',
          full: 'مستلم',
          manualNumber: 'الرقم الخاص',
          pouchID: 'رقم المحفظة'
        }
      }
    })

    const po = ref(new PurchaseOrder())
    const http = useHTTP()
    const loading = ref(true)
    const error = ref({title: '', body: ''})
    const route = useRoute()
    const router = useRouter()

    onBeforeMount(async () => {
      const url = `${PurchaseOrder.ENDPOINT}/${route.params.id}`

      try {
        const resp = await http.get<PurchaseOrder>(url)
        po.value = PurchaseOrder.from(resp)
      } catch (errorResp) {
        error.value.title = errorResp.title
        error.value.body = errorResp.body
      }

      loading.value = false
    })

    const irStatuses = computed(() => {
      let remaining = po.value.orderItems.reduce(
          (sum, poi) => sum + (poi.quantity || 0),
          0
      );

      return po.value.inventoryReceivings.map((item) => {
        const irQuantity = item.inventoryReceivingItems.reduce(
            (sum, iri) => sum + (iri.quantity || 0),
            0
        );

        if (remaining > 0) {
          if (irQuantity < remaining) {
            remaining -= irQuantity;
            return 'partial';
          } else {
            remaining = 0;
            return 'full';
          }
        }

        return 'full'; // Default case if no remaining
      });
    });

    const isFullyReceived = computed(() => {
      const quantity = po.value.orderItems.reduce(
          (sum, poi) => sum + (poi.quantity || 0),
          0
      );

      const receivedQuantity = po.value.orderItems.reduce(
          (sum, poi) => sum + (poi.receivedQuantity || 0),
          0
      );

      if (receivedQuantity >= quantity) {
        return true
      }

      return false;
    });

    async function updateDueDate(event: Date) {
      try {
        const resp = await http.put(`${PurchaseOrder.ENDPOINT}/${po.value.id}/updateDueDate`, {dueDate: dayjs(event).format('YYYY-MM-DD')})
      } catch (e) {
        console.log(e)
      }
    }

    return {
      po,
      loading,
      error,
      t,
      formatPostgresDate,
      router,
      irStatuses,
      isFullyReceived,
      updateDueDate
    }
  }
})
</script>

<style scoped>
/* table */
table {
  font-family: arial, sans-serif;
  border-collapse: collapse;
}

td,
th {
  border: 1px solid #dddddd;
  text-align: center;
  padding: 4px;
}

tr:nth-child(odd) {
  background-color: #ebebeb;
}
</style>
